import { Stack, TextField, MenuItem, FormControlLabel, Switch, Button } from '@mui/material';
import { Clear } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { clientsApi } from '../api/clients.api';

export interface AmFilters {
  search?: string;
  status?: string;
  clientId?: number | '';
  city?: string;
  discom?: string;
  serviceType?: string;
  propertyType?: string;
  ownerType?: string;
  connectionType?: string;
  from?: string;
  to?: string;
  expiringOnly?: boolean;
}

export type AmFilterKey = 'search' | 'status' | 'client' | 'city' | 'discom' | 'serviceType' | 'property' | 'owner' | 'connection' | 'dates' | 'expiring';

const PROPERTY_TYPES = ['Commercial', 'Residential', 'Industrial', 'Highway', 'Mall'];
const OWNER_TYPES: Array<[string, string]> = [['single', 'Single'], ['company', 'Company'], ['multiple', 'Multiple']];
const CONNECTION_TYPES = ['LT', 'HT'];

interface Props {
  value: AmFilters;
  onChange: (next: AmFilters) => void;
  show: AmFilterKey[];
  statusOptions?: string[];
  serviceTypes?: readonly string[];
  searchPlaceholder?: string;
}

const human = (s: string) => s.replace(/_/g, ' ');

/** Reusable filter toolbar for Asset Management station-ops pages (+ dashboard). */
export function AmFilterBar({ value, onChange, show, statusOptions = [], serviceTypes = [], searchPlaceholder = 'Search…' }: Props) {
  const wantClients = show.includes('client');
  const { data: clients } = useQuery({ queryKey: ['clients', 'all'], queryFn: () => clientsApi.list({ limit: 500 }), enabled: wantClients });
  const set = (patch: Partial<AmFilters>) => onChange({ ...value, ...patch });
  const active = Object.values(value).some((v) => v !== undefined && v !== '' && v !== false);

  return (
    <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap" useFlexGap alignItems="center">
      {show.includes('search') && (
        <TextField size="small" placeholder={searchPlaceholder} value={value.search ?? ''} onChange={(e) => set({ search: e.target.value })} sx={{ minWidth: 220 }} />
      )}
      {show.includes('status') && (
        <TextField select size="small" label="Status" value={value.status ?? ''} onChange={(e) => set({ status: e.target.value })} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          {statusOptions.map((s) => <MenuItem key={s} value={s}>{human(s)}</MenuItem>)}
        </TextField>
      )}
      {show.includes('client') && (
        <TextField select size="small" label="Client" value={value.clientId ?? ''} onChange={(e) => set({ clientId: e.target.value ? Number(e.target.value) : '' })} sx={{ minWidth: 180 }}>
          <MenuItem value="">All clients</MenuItem>
          {(clients?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.clientName}</MenuItem>)}
        </TextField>
      )}
      {show.includes('city') && (
        <TextField size="small" label="City" value={value.city ?? ''} onChange={(e) => set({ city: e.target.value })} sx={{ minWidth: 130 }} />
      )}
      {show.includes('property') && (
        <TextField select size="small" label="Property" value={value.propertyType ?? ''} onChange={(e) => set({ propertyType: e.target.value })} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          {PROPERTY_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
      )}
      {show.includes('owner') && (
        <TextField select size="small" label="Owner type" value={value.ownerType ?? ''} onChange={(e) => set({ ownerType: e.target.value })} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          {OWNER_TYPES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
        </TextField>
      )}
      {show.includes('connection') && (
        <TextField select size="small" label="Connection" value={value.connectionType ?? ''} onChange={(e) => set({ connectionType: e.target.value })} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          {CONNECTION_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
      )}
      {show.includes('discom') && (
        <TextField size="small" label="DISCOM" value={value.discom ?? ''} onChange={(e) => set({ discom: e.target.value })} sx={{ minWidth: 130 }} />
      )}
      {show.includes('serviceType') && (
        <TextField select size="small" label="Service Type" value={value.serviceType ?? ''} onChange={(e) => set({ serviceType: e.target.value })} sx={{ minWidth: 200 }}>
          <MenuItem value="">All</MenuItem>
          {serviceTypes.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      )}
      {show.includes('dates') && (
        <>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={value.from ?? ''} onChange={(e) => set({ from: e.target.value })} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={value.to ?? ''} onChange={(e) => set({ to: e.target.value })} />
        </>
      )}
      {show.includes('expiring') && (
        <FormControlLabel control={<Switch checked={Boolean(value.expiringOnly)} onChange={(e) => set({ expiringOnly: e.target.checked })} />} label="Expiring ≤30d" />
      )}
      {active && <Button size="small" startIcon={<Clear />} onClick={() => onChange({})}>Clear</Button>}
    </Stack>
  );
}
